import React, { forwardRef, useContext } from 'react';
import { withStyles, Divider, makeStyles, hexToRgb } from '@material-ui/core';
import Carousel from '../../carousel/Carousel';
import DataContext from '../../data/DataContext';
import { openLink } from '../../util/utilNative';
import NoColor from '../../../asset/img/btn_menu_close_pre.png';

const styles = (theme) => ({
  root: {
    position: 'relative',
    letterSpacing: '-0.0313rem',
    margin: '0.375rem 0rem',
    zIndex: 10,
  },
  carousel: {
    width: '100%',
    height: '9rem',
  },
  listitem: {
    padding: '0px',
    display: 'flex',
    margin: '0 0 0.375rem 0',
  },
  card: {
    width: '20rem',
    height: '100%',
    flex: 'auto',
    background: '#fff',
    border: '0.0625rem solid #d7d7d7',
    borderRadius: '0.375rem',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    margin: '0 0.375rem 0 0',
    boxShadow: '0 0.125rem 0.375rem 0 rgba(0, 0, 0, 0.1)',
    position: 'relative',
  },
  thumbnail: {
    minHeight: '12.688rem',
    padding: '1rem 0 0 0',
    margin: '1rem 0 0 0',
    background: 'none',
    border: 'none',
  },
  thumbnailImage: {
    width: 'auto',
    height: '12.25rem',
    cursor: 'pointer',
  },
  noThumbnailImage: {
    padding: '3.125rem',
  },
  info: {
    flexGrow: 1,
    minHeight: '9.688rem',
  },
  infoTitle: {
    display: 'flex',
    justifyContent: 'flex-start',
    fontSize: theme.font.size16, // "15px",
    fontWeight: 'bold',
    color: theme.color.text_color.black,
    textAlign: 'left',
    padding: '0rem 1.25rem',
    minHeight: '2.875rem',
    lineHeight: '1.38',
  },
  infoDescription: {
    display: 'flex',
    justifyContent: 'flex-start',
    fontSize: theme.font.size12,
    fontWeight: 'normal',
    color: theme.color.text_color.grey,
    padding: '0rem 1.25rem',
    height: '1.188rem',
  },
  horizontalDivider: {
    width: 'auto',
    height: '0.0625rem',
    backgroundColor: theme.color.divider.grey,
    margin: '0.7188rem 1.25rem',
  },
  productPriceInfo: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    margin: '0 1.25rem',
  },
  price: {
    color: '#000',
    fontSize: theme.font.size16,
    fontWeight: '700',
    textAlign: 'right',
  },
  productPrice: {
    fontSize: theme.font.size12,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0.75rem 1.25rem 1.25rem',
  },
  buttons: {
    height: '2.5rem',
    width: '100%',
  },
  btnSubs: {
    borderRadius: '0.375rem',
    backgroundColor: '#EA1917',
    color: '#fff',
    textAlign: 'center',
    width: '100%',
    height: '100%',
    fontSize: theme.font.size14,
    fontWeight: '700',
    lineHeight: '2.5rem',
    border: 'none',
  },
  btnDisabled: {
    backgroundColor: '#666',
  },
  productSpec: {
    margin: '0 1.25rem',
    minHeight: '4.625rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'space-between',
  },
  productSpecDesc: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: '1.5rem',
  },
  productSpecLabel: {
    fontSize: theme.font.size12,
    fontWeight: 'normal',
    color: '#666',
    textAlign: 'left',
  },
  productSpecValue: {
    fontSize: theme.font.size12,
    fontWeight: 'normal',
    color: '#000',
    textAlign: 'right',
  },
  productSpecValueText: {
    paddingLeft: '0.3125rem',
  },
  colorValueSibling: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  productBadge: {
    display: 'block',
    color: '#666',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 'normal',
  },
});

// product_image_url|product_image_mobile_url (제품이미지 url)
const isURLValid = (strUrl) => {
  let expUrl = /^http[s]?:\/\/([\S]{3,})/i;
  return expUrl.test(strUrl);
};

const noImgPath = `${window.location.protocol}//${window.location.host}/image/no-image`;
const handleImgError = (e) => {
  e.target.style.padding = '3.125rem';
  e.target.src = `${noImgPath}.png`;
};

const SubsPkCard = forwardRef((props, ref) => {
  const { classes, data } = props;
  const context = useContext(DataContext);
  const isMobile = context.userAgent.deviceType === 'mobile';

  let cardList = data.items.map((item, key) => {
    const productImgUrl = isMobile
        ? item.link.product_image_mobile_url
        : item.link.product_image_url,
      productTitle = item.link.product_title,
      productName = item.link.product_name,
      productLinkUrl = item?.link?.product_link_url,
      productLinkType = item?.link?.type || 'external'; // 'external'

    const handleOpenLink = (type, url) => {
      if (url) {
        openLink(context, type, url);
      }
    };

    // 제품 기본 정보: volume(용량), managementType(기본형 케어십)
    const productSpecArr = item?.product_spec || [];

    // 제품 기본 정보: siblingList(색상)
    const productColorArr = item?.product_color?.value || [];
    const productColors = productColorArr.slice(0, 3); // 컬러 3개까지만 자르기
    const productColorsLen = productColorArr.length;

    // 1회차 월 요금
    const productMasterPrice = item?.product_price?.product_master_price;

    // 멤버십 포인트
    const productBadge = item?.product_badge;

    // 구독신청
    const buttonsArr = item?.buttons || [];

    return (
      <div key={`cardList${key}`} className={classes.listitem}>
        <div className={classes.card}>
          <button
            className={classes.thumbnail}
            onClick={() => handleOpenLink(productLinkType, productLinkUrl)}
          >
            {productImgUrl && isURLValid(productImgUrl) ? (
              <img
                className={classes.thumbnailImage}
                src={productImgUrl}
                alt={`${productName} 상세 바로가기`}
                onError={handleImgError}
              />
            ) : (
              <img
                className={`${classes.thumbnailImage} ${classes.noThumbnailImage} `}
                src={`${noImgPath}.png`}
              />
            )}
          </button>
          <div className={classes.info}>
            {/* HTML TAG가 들어간 경우 처리 */}
            <div
              className={classes.infoTitle}
              dangerouslySetInnerHTML={{ __html: productTitle }}
              role={'text'}
            />
            <div
              className={classes.infoDescription}
              dangerouslySetInnerHTML={{ __html: productName }}
            />

            <Divider
              orientation={'horizontal'}
              className={classes.horizontalDivider}
              component={'div'}
              disabled
            />

            <div className={classes.productSpec}>
              <div className={classes.productSpecDesc}>
                <div className={classes.productSpecLabel}>색상</div>
                {productColorArr && (
                  <div className={classes.colorValueSibling}>
                    {productColors.map((color, idx) => {
                      // [Conditional Rendering] Props
                      const useStyles = makeStyles({
                        colorValue: (props) => ({
                          background: props.background,
                          width: '0.875rem',
                          height: '0.875rem',
                          textIndent: '-62499.9375rem',
                          borderRadius: '100%',
                          marginLeft: '0.3125rem',
                          border: '1px solid rgba(0,0,0,.12)',
                        }),
                        // colorValue: {
                        //   // CSS property
                        //   color: props => props.color,
                        // },
                      });

                      let colorChip = '';

                      switch (color) {
                        case 'objet_pink_1':
                          colorChip = '#e6c5c1';
                          break;
                        case 'objet_brown':
                          colorChip = '#c8b2a6';
                          break;
                        case 'objet_pink_brown':
                          colorChip =
                            'linear-gradient(to bottom, #e6c5c1 50%, #c8b2a6 50%)';
                          break;
                        case 'objet_brown_botanic':
                          colorChip =
                            'linear-gradient(to bottom, #c8b2a6 50%, #eEe9e5 50%)';
                          break;
                        case 'objet_cream_lavender':
                          colorChip = '#c1b8cc';
                          break;
                        case 'objet_cream_peach':
                          colorChip = '#e1a990';
                          break;
                        case 'objet_cream_lemon':
                          colorChip = '#dfd298';
                          break;
                        case 'objet_cream_sky':
                          colorChip = '#e4e8ec';
                          break;
                        case 'blue':
                          colorChip = '#102948';
                          break;
                        case 'objet_botanic':
                          colorChip = '#eee9e5';
                          break;
                        case 'beige_beige':
                          colorChip = '#eee9e5';
                          break;
                        case 'calming-beige':
                          colorChip = '#dfdad4';
                          break;
                        case 'objet_cream_white':
                          colorChip = '#f5f3f1';
                          break;
                        case 'objet_green':
                          colorChip = '#293C2E';
                          break;
                        case 'objet_grey':
                          colorChip = '#cdccc7';
                          break;
                        case 'objet_pink':
                          colorChip = '#e3c3c0';
                          break;
                        case 'pink_beige':
                          colorChip =
                            'linear-gradient(to bottom, #e6c4c2 50%, #eee9e5 50%)';
                          break;
                        case 'white':
                          colorChip = '#fff';
                          break;
                        case 'objet_botanic1':
                          colorChip = '#eee9e5';
                          break;
                        case 'clay_brown':
                          colorChip = '#c8b2a6';
                          break;
                        case 'dark_gray':
                          colorChip = '#414146';
                          break;
                        case 'storm_gray':
                          colorChip = '#6e7d9c';
                          break;
                        case 'nature-beige':
                          colorChip = '#dad2ca';
                          break;
                        case 'nature-green':
                          colorChip = '#4c5351';
                          break;
                        case 'navy_cream-gray':
                          colorChip =
                            'linear-gradient(to bottom, #3a4151 50%, #dad9db 50%)';
                          break;
                        case 'manhattan_manhattan':
                          colorChip =
                            'linear-gradient(to bottom, #414146 50%, #414146 50%)';
                          break;
                        case 'beige_claybrown':
                          colorChip =
                            'linear-gradient(to bottom, #eee9e5 50%, #c8b2a6 50%)';
                          break;
                        case 'cream-gray_cream-white':
                          colorChip =
                            'linear-gradient(to bottom, #dad9db 50%, #fffcfc 50%)';
                          break;
                        case 'cream-remon_cream-white':
                          colorChip =
                            'linear-gradient(to bottom, #dfd298 50%, #fffcfc 50%)';
                          break;
                        case 'cream-sky_cream-white':
                          colorChip =
                            'linear-gradient(to bottom, #e4e8ec 50%, #fffcfc 50%)';
                          break;
                        case 'cream-peach_beige':
                          colorChip =
                            'linear-gradient(to bottom, #e1a990 50%, #eee9e5 50%)';
                          break;
                        case 'cream-white_navy':
                          colorChip =
                            'linear-gradient(to bottom, #fffcfc 50%, #3a4151 50%)';
                          break;
                        case 'navy_cream-white':
                          colorChip =
                            'linear-gradient(to bottom, #3a4151 50%, #fffcfc 50%)';
                          break;
                        case 'cream-white_cream-gray':
                          colorChip =
                            'linear-gradient(to bottom, #fffcfc 50%, #dad9db 50%)';
                          break;
                        case 'creamwhite_creamwhite':
                          colorChip =
                            'linear-gradient(to bottom, #fffcfc 50%, #fffcfc 50%)';
                          break;
                        case 'claybrown_beige':
                          colorChip =
                            'linear-gradient(to bottom, #c8b2a6 50%, #eee9e5 50%)';
                          break;
                        case 'claybrown_claybrown':
                          colorChip =
                            'linear-gradient(to bottom, #c8b2a6 50%, #c8b2a6 50%)';
                          break;
                        case 'pink_claybrown':
                          colorChip =
                            'linear-gradient(to bottom, #e6c5c1 50%, #c8b2a6 50%)';
                          break;
                        case 'pink_pink':
                          colorChip =
                            'linear-gradient(to bottom, #e6c5c1 50%, #e6c5c1 50%)';
                          break;
                        case 'claybrown':
                          colorChip = '#c8b2a6';
                          break;
                        case 'clay_brown':
                          colorChip = '#c8b2a6';
                          break;
                        case 'objet_claypink':
                          colorChip = '#9a8786';
                          break;
                        case 'prime-silver':
                          colorChip = '#bfc0c6';
                          break;
                        case 'nature-beige_green':
                          colorChip =
                            'linear-gradient(to bottom, #e9e3e1 50%, #415254 50%)';
                          break;
                        case 'nature-green_beige':
                          colorChip =
                            'linear-gradient(to bottom, #42514c 50%, #eae4df 50%)';
                          break;
                        case 'manhattan_silver':
                          colorChip =
                            'linear-gradient(to bottom, #32373a 50%, #b3b4b8 50%)';
                          break;
                        case 'essence_white':
                          colorChip = '#f7f6f6';
                          break;
                        case 'essence_graphite':
                          colorChip = '#434447';
                          break;
                        case 'cream_yellow':
                          colorChip = '#edd373';
                          break;
                        case 'cream_rose':
                          colorChip = '#d85e66';
                          break;
                        case 'c_gold':
                          colorChip = '#c1a78e';
                          break;
                        case 'c_purple':
                          colorChip = '#c4b6d4';
                          break;
                        case 'washtower_glossy-white':
                          colorChip = '#f0f0f2';
                          break;
                        case 'topaz_green':
                          colorChip = '#b8c0ac';
                          break;
                        case 'neptune-blue':
                          colorChip = '#474a53';
                          break;
                        case 'stone_silver':
                          colorChip = '#dfdfe1';
                          break;
                        case 'nature-manhattan-midnight':
                          colorChip = '#414146';
                          break;
                        case 'mist-clay-brown':
                          colorChip = '#d1c7bd';
                          break;
                        case 'nature_cream_gray-nature_green':
                          colorChip =
                            'linear-gradient(to bottom, #d2cfd6 50%, #3a4848 50%)';
                          break;
                        case 'nature_navy-nature_green':
                          colorChip =
                            'linear-gradient(to bottom, #2f375b 50%, #3a4848 50%)';
                          break;
                        case 'nature-beige_nature-green':
                          colorChip =
                            'linear-gradient(to bottom, #eeede9 50%, #3a4848 50%)';
                          break;
                        case 'nature_cream_gray-nature_beige':
                          colorChip =
                            'linear-gradient(to bottom, #d2cfd6 50%, #eeede9 50%)';
                          break;
                        case 'nature_navy-nature_beige':
                          colorChip =
                            'linear-gradient(to bottom, #2f375b 50%, #eeede9 50%)';
                          break;
                        case 'nature_navy-nature_cream_gray':
                          colorChip =
                            'linear-gradient(to bottom, #2f375b 50%, #d2cfd6 50%)';
                          break;
                        case 'nature_beige-nature_cream_gray':
                          colorChip =
                            'linear-gradient(to bottom, #eeede9 50%, #d2cfd6 50%)';
                          break;
                        case 'nature_cream_gray-nature_cream_gray':
                          colorChip =
                            'linear-gradient(to bottom, #d2cfd6 50%, #d2cfd6 50%)';
                          break;
                        case 'nature_green-nature_cream_gray':
                          colorChip =
                            'linear-gradient(to bottom, #3a4848 50%, #d2cfd6 50%)';
                          break;
                        case 'nature_cream_gray-nature_navy':
                          colorChip =
                            'linear-gradient(to bottom, #d2cfd6 50%, #2f375b 50%)';
                          break;
                        case 'nature_navy-nature_navy':
                          colorChip =
                            'linear-gradient(to bottom, #2f375b 50%, #2f375b 50%)';
                          break;
                        case 'nature_green-nature_navy':
                          colorChip =
                            'linear-gradient(to bottom, #3a4848 50%, #2f375b 50%)';
                          break;
                        case 'nature_beige-nature_navy':
                          colorChip =
                            'linear-gradient(to bottom, #eeede9 50%, #2f375b 50%)';
                          break;
                        case 'objet_cream_lavender-objet_cream_lavender':
                          colorChip =
                            'linear-gradient(to bottom, #c1b8cc 50%, #c1b8cc 50%)';
                          break;
                        case 'objet_cream_peach-objet_cream_peach':
                          colorChip =
                            'linear-gradient(to bottom, #e1a990 50%, #e1a990 50%)';
                          break;
                        case 'objet_cream_lemon-objet_cream_lemon':
                          colorChip =
                            'linear-gradient(to bottom, #dfd298 50%, #dfd298 50%)';
                          break;
                        case 'objet_cream_sky-objet_cream_sky':
                          colorChip =
                            'linear-gradient(to bottom, #e4e8ec 50%, #e4e8ec 50%)';
                          break;
                        case 'calming_clay_mint':
                          colorChip = '#7ca6b4';
                          break;
                        case 'calming-pink':
                          colorChip = '#f0ddd9';
                          break;
                        case 'calming_essence_white':
                          colorChip = '#f6f8f5';
                          break;
                        case 'washing_lily-white':
                          colorChip = '#f9fbfa';
                          break;
                        default:
                          // 컬러 리스트에 없을때??
                          colorChip = `url(${NoColor}) center center / 0.875rem no-repeat`;
                          break;
                      }

                      // Simulated props for the purpose of the example
                      const props = { background: colorChip };
                      // Pass the props as the first argument of useStyles()
                      const classes = useStyles(props);

                      return (
                        <div
                          key={`colorValueSibling${idx}`}
                          className={classes.colorValue}
                        >
                          {color}
                        </div>
                      );
                    })}
                    {productColorsLen > 3 && (
                      <div
                        className={`${classes.productSpecValue} ${classes.productSpecValueText}`}
                      >
                        {`외 ${productColorsLen - 3}`}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {productSpecArr.map((it, idx) => {
                const productSpecLabel = it.label,
                  productSpecValue = it.value;

                return (
                  <div key={`productSpec${idx}`} className={classes.productSpecDesc}>
                    <div className={classes.productSpecLabel}>{productSpecLabel}</div>
                    <div className={classes.productSpecValue}>{productSpecValue}</div>
                  </div>
                );
              })}
            </div>

            <Divider
              orientation={'horizontal'}
              className={classes.horizontalDivider}
              component={'div'}
              disabled
            />

            <div className={classes.productPriceInfo}>
              <div className={`${classes.price} ${classes.productPrice}`}>
                1회차 월 요금
              </div>
              <div className={classes.price}>
                {`월 ${productMasterPrice}원~`}
                <em className={classes.productBadge}>{productBadge}</em>
              </div>
            </div>

            <div className={classes.buttonContainer}>
              {buttonsArr.map((it, idx) => {
                const label = it.label,
                  type = it.type,
                  parametersAction = it.parameters.action,
                  parametersEnable = it.parameters.enable,
                  parametersUrl = isMobile ? it.parameters.mobile_url : it.parameters.url;

                return (
                  <div key={`buttons${idx}`} className={classes.buttons}>
                    <button
                      className={`${classes.btnSubs} ${
                        !parametersEnable && classes.btnDisabled
                      }`}
                      onClick={() => handleOpenLink(type, parametersUrl)}
                    >
                      {label}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  });

  return cardList;
});

function SubpackageCard(props) {
  const { classes, data } = props;
  const context = useContext(DataContext);

  console.log('====== 테스트 SubpackageCard :: data ======', data);

  return (
    <div className={classes.root}>
      <Carousel
        className={classes.carousel}
        component={withStyles(styles)(SubsPkCard)}
        data={data}
        ariaLabel={context.string.aria.product}
      />
    </div>
  );
}

export default withStyles(styles)(SubpackageCard);
